import { useEffect, useState } from "react";

/**
 * BuildVersion — discreet build identifier displayed in the sidebar footer.
 *
 * Reads from `/version.json` (generated by `scripts/bump-sw-version.js` at
 * build time). In dev/preview the file ships with `{ version: "dev" }`, so
 * the badge silently shows "dev". In production each deploy writes a fresh
 * `{ sha, builtAt }` derived from the current git SHA + timestamp.
 *
 * Visual: tiny gray text `v.{sha} · {dd mmm yyyy}` with hover tooltip.
 * Click copies the full version string to clipboard for support tickets.
 */
export function BuildVersion({ collapsed = false }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/version.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled && data) setInfo(data); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  if (!info) return null;

  const sha = info.sha || "dev";
  let prettyDate = "";
  if (info.builtAt) {
    try {
      const d = new Date(info.builtAt);
      prettyDate = d.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch { /* keep empty */ }
  }

  const handleClick = async () => {
    try {
      await navigator.clipboard.writeText(info.version || sha);
    } catch { /* ignore */ }
  };

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={handleClick}
        title={`v.${sha}${prettyDate ? ` · ${prettyDate}` : ""} (clic para copiar)`}
        className="text-[9px] text-muted-foreground/50 hover:text-muted-foreground font-mono py-1 cursor-pointer transition-colors w-full text-center"
        data-testid="build-version-collapsed"
      >
        {sha === "dev" ? "·" : sha.slice(0, 4)}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      title={`Build ${info.version || sha}${prettyDate ? ` · ${prettyDate}` : ""} (clic para copiar)`}
      className="text-[10px] text-muted-foreground hover:text-foreground font-mono py-1 px-2 cursor-pointer transition-colors w-full text-left truncate"
      data-testid="build-version"
    >
      v.{sha}
      {prettyDate && <span className="ml-1.5 text-muted-foreground">· {prettyDate}</span>}
    </button>
  );
}

export default BuildVersion;
